import { backendApiClient } from '@geneo2-web/services-clients';
import { PAL_BASE_URL } from '@geneo2-web/shared-ui';
import { AxiosRequestConfig } from 'axios';
import { IKnowledgeConcept } from '../KnowledgeConcepts';

const GET_MODULE_EXTERNAL_MAPPING_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/modules/readiness`;

export interface IPalModuleStatus {
  moduleId: number;
  externalId: string;
  isReady: boolean;
  isStarted: boolean;
}

export interface IModuleExternalMappingResponse {
  data: IPalModuleStatus[];
}

export async function getModuleReadiness(
  body: {
    module_ids: number[];
  },
  options: AxiosRequestConfig = {}
): Promise<IModuleExternalMappingResponse> {
  options.headers = {
    ...options.headers,
    'x-learning-mode': 'adaptive',
  };
  return await backendApiClient.post<IModuleExternalMappingResponse>(
    GET_MODULE_EXTERNAL_MAPPING_PATH,
    body,
    options
  );
}

const GET_KNOWLEDGE_CONCEPT_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/modules/knowledge/concepts/graph`;

export async function getKnowledgeConceptGraph(
  body: {
    module_id: string;
    prereq_depth?: number;
  },
  options: AxiosRequestConfig = {}
): Promise<{ data: IKnowledgeConcept }> {
  if (!body.prereq_depth) {
    body.prereq_depth = 2;
  }
  return await backendApiClient.get<{ data: IKnowledgeConcept }>(
    GET_KNOWLEDGE_CONCEPT_PATH,
    body,
    options
  );
}

const GET_MODULE_MASTERY_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/modules/mastery`;

export type MasteryLevel =
  | 'not_started'
  | 'beginner'
  | 'developing'
  | 'proficient'
  | 'mastered';

export type ConceptStatus =
  | 'not_assessed'
  | 'struggling'
  | 'beginning'
  | 'developing'
  | 'confident'
  | 'mastered';

export interface IModuleMasteryProgress {
  module_id: string;
  module_name: string;
  completion_percentage: number;
  accuracy_percentage: number;
  overall_mastery_level: MasteryLevel | string;
  theta_aggregate: number;
  total_questions_answered: number;
  total_correct: number;
  concepts_mastered: number;
  concepts_total: number;
  los_mastered: number;
  los_total: number;
  total_practice_time_seconds: number;
  upi_score_aggregate: number;
  last_activity_at: string;
}

export interface IConceptState {
  concept_id: string;
  concept_name: string;
  mastery_level: MasteryLevel | string;
  concept_status?: ConceptStatus | string;
  mastery_percentage: number;
  practice_count: number;
  theta: number;
  theta_se: number;
  readiness: number;
  n: number;
  was_mastered: boolean;
  last_practice_at: string;
  upi_score: number;
  upi_score_gain: number;
  upi_gain_period_key: string;
}

export interface ILoMastery {
  lo_id: string;
  lo_name: string;
  mastery_probability: number;
  attempts: number;
  correct_count: number;
  last_attempt_at: string;
}

export interface IModuleMasteryData {
  learner_id: string;
  module_id: string;
  module_name: string;
  progress: IModuleMasteryProgress;
  concept_states: IConceptState[];
  lo_mastery: ILoMastery[];
}

export interface IModuleMasteryResponse {
  data: IModuleMasteryData;
}

export async function getModuleMasteryApi(
  params: {
    module_id: string;
  },
  options: AxiosRequestConfig = {}
): Promise<IModuleMasteryResponse> {
  return await backendApiClient.get<IModuleMasteryResponse>(
    GET_MODULE_MASTERY_PATH,
    params,
    options
  );
}

const GET_ONGOING_JOURNEYS_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/journeys`;

export interface IOngoingJourneyProgress {
  module_id: string;
  module_name: string;
  theta_aggregate: number;
  total_questions_answered: number;
  total_correct: number;
  concepts_mastered: number;
  concepts_total: number;
  los_mastered: number;
  los_total: number;
  total_practice_time_seconds: number;
  upi_score_aggregate: number;
  mastery_percentage: number;
  module_started_at: string;
  last_activity_at: string;
}

export interface GeneoModuleInfo {
  geneo_module_id?: number;
  geneoModule?: {
    poster_image_url?: string;
    book_id?: number;
    name?: string;
  };
}

export interface IOngoingJourneyModule extends GeneoModuleInfo {
  module_id: string;
  module_name: string;
  progress: IOngoingJourneyProgress;
  concepts_total: number;
  concepts_with_state: number;
  concepts_mastered: number;
}

export interface IOngoingJourneysResponse {
  learner_id: string;
  modules: IOngoingJourneyModule[];
  not_found: string[];
}

export async function getOngoingJourneys(
  params: {
    sort_by?: string;
    sort_order?: string;
    page?: number;
    page_size?: number;
    book_id?: number;
  },
  options: AxiosRequestConfig = {}
): Promise<IOngoingJourneysResponse> {
  return await backendApiClient.get<IOngoingJourneysResponse>(
    GET_ONGOING_JOURNEYS_PATH,
    params,
    options
  );
}

const CLEAR_JOURNEYS_PATH = `${
  PAL_BASE_URL || ''
}/adaptive/learners/journeys/clear`;

export async function clearOngoingJourneysApi(
  options: AxiosRequestConfig = {}
): Promise<unknown> {
  return await backendApiClient.post<unknown>(
    CLEAR_JOURNEYS_PATH,
    undefined,
    options
  );
}
